import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Stop, SpeakerHigh } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEMALE = /female|samantha|victoria|karen|moira|tessa|serena|zira|susan|hazel|libby|sonia|aria|jenny|fiona|kate|emma/i;
const MALE = /\bmale\b|daniel|alex|fred|george|ryan|guy|david|mark|james|oliver|thomas|arthur|rishi/i;

function pickVoices() {
  const en = window.speechSynthesis.getVoices().filter((v) => v.lang?.toLowerCase().startsWith("en"));
  const f = en.find((v) => FEMALE.test(v.name)) || en[0];
  const m = en.find((v) => MALE.test(v.name) && v !== f) || en.find((v) => v !== f) || f;
  return { F: f, M: m };
}

export const ListeningPlayer = ({ script, audioUrl, playsUsed, maxPlays = 3, onPlayed, context, testId = "test-listening-player" }) => {
  const [playing, setPlaying] = useState(false);
  const voices = useRef({});
  const audioEl = useRef(null);
  const left = maxPlays - playsUsed;

  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    const load = () => { voices.current = pickVoices(); };
    load();
    window.speechSynthesis.addEventListener?.("voiceschanged", load);
    return () => {
      window.speechSynthesis.removeEventListener?.("voiceschanged", load);
      window.speechSynthesis.cancel();
      audioEl.current?.pause();
    };
  }, []);

  const playMp3 = () => {
    if (!audioEl.current) {
      audioEl.current = new Audio(`${BACKEND_URL}${audioUrl}`);
      audioEl.current.onended = () => setPlaying(false);
      audioEl.current.onerror = () => { setPlaying(false); toast.error("Could not play the recording."); };
    }
    onPlayed();
    setPlaying(true);
    audioEl.current.currentTime = 0;
    audioEl.current.play().catch(() => setPlaying(false));
  };

  const play = () => {
    if (audioUrl) return left > 0 && playMp3();
    if (!("speechSynthesis" in window)) return toast.error("Your browser does not support text-to-speech.");
    if (left <= 0) return;
    window.speechSynthesis.cancel();
    onPlayed();
    setPlaying(true);
    script.forEach(([speaker, line], i) => {
      const u = new SpeechSynthesisUtterance(line);
      const v = voices.current[speaker];
      if (v) u.voice = v;
      u.lang = v?.lang || "en-GB";
      u.rate = 0.92;
      u.pitch = speaker === "M" ? 0.85 : 1.15;
      if (i === script.length - 1) u.onend = () => setPlaying(false);
      u.onerror = () => setPlaying(false);
      window.speechSynthesis.speak(u);
    });
  };

  const stop = () => {
    if (audioUrl) audioEl.current?.pause();
    else window.speechSynthesis.cancel();
    setPlaying(false);
  };

  return (
    <div className="neu-inset rounded-2xl p-5" data-testid={testId}>
      {context && <p className="text-sm text-foreground/80 mb-4" data-testid="test-listening-context">{context}</p>}
      <div className="flex items-center gap-4">
        <button
          onClick={playing ? stop : play}
          disabled={!playing && left <= 0}
          data-testid="test-listening-play-btn"
          className="neu-btn-primary w-14 h-14 flex items-center justify-center shrink-0 disabled:opacity-40"
        >
          {playing ? <Stop size={20} weight="fill" /> : <Play size={20} weight="fill" className="translate-x-0.5" />}
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <SpeakerHigh size={16} weight="bold" />
            {playing ? "Playing recording…" : left > 0 ? "Press play to hear the recording" : "No plays left"}
          </div>
          <div className="mt-1 text-xs text-muted-foreground font-mono-fig" data-testid="test-listening-plays-left">
            {left} of {maxPlays} plays left{audioUrl ? " · recorded audio" : ""}
          </div>
        </div>
      </div>
    </div>
  );
};
